import { Component, Input } from '@angular/core';
import { ApiService } from '../../api.service';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { ProjectConfiguration } from '@models/ProjectConfiguration';
import { RouterModule } from '@angular/router';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@Component({
  selector: 'app-publication-status',
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    RouterModule,
    MatProgressBarModule
  ],
  templateUrl: './publication-status.component.html',
  styleUrl: './publication-status.component.scss'
})
export class PublicationStatusComponent {
  @Input() public commitSha: string | undefined;
  @Input() public project!: ProjectConfiguration;
  status: 'queued' | 'in_progress' | 'completed' | undefined;
  conclusion: 'action_required' | 'cancelled' | 'failure' | 'neutral' | 'success' | 'skipped' | 'stale' | 'timed_out' | undefined;
  private intervalId: NodeJS.Timeout | undefined;

  public showFallbackPublicationResult = false;

  constructor(private api: ApiService) { }

  async ngOnChanges() {
    if (this.commitSha) {
      this.startPolling();
    }
  }

  async startPolling() {
    try {
      this.intervalId = setInterval(async () => {
        if (this.repository && this.commitSha) {
          // console.log("Polling for status of", this.repository, this.commitSha);
          const result = await this.api.getActionsForCommit(this.repository, this.commitSha);
          if (!result.workflow_runs) {
            console.log(result);
          }
          if (result.workflow_runs.length === 0) {
            /// this might be because the job is about to start; so keep checking
            this.status = undefined;
          } else {
            const run = result.workflow_runs[0];
            // console.log(result);

            this.status = run.status;
            this.conclusion = run.conclusion;

            if (this.status === 'completed') {
              clearInterval(this.intervalId);
            }
          }
        }
      }, 3000); // Poll every 3 seconds
    } catch (e) {
      console.error(e);
    }
  }


  get repository(): string | undefined {
    if (this.project) {
      return this.project.repositoryName;
    }
    return undefined;
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'queued':
        return 'status-queued';
      case 'in_progress':
        return 'status-in-progress';
      case 'completed':
        return 'status-completed';
      default:
        return '';
    }
  }

  getStatusMessage(status: string): string {
    switch (status) {
      case 'queued':
        return 'Job is queued';
      case 'in_progress':
        return 'In progress...';
      case 'completed':
        return 'Completed';
      default:
        return 'No information available yet';
    }
  }

  getConclusionClass(conclusion: string): string {
    switch (conclusion) {
      case 'action_required':
        return 'conclusion-action-required';
      case 'cancelled':
        return 'conclusion-cancelled';
      case 'failure':
        return 'conclusion-failure';
      case 'neutral':
        return 'conclusion-neutral';
      case 'success':
        return 'conclusion-success';
      case 'skipped':
        return 'conclusion-skipped';
      case 'stale':
        return 'conclusion-stale';
      case 'timed_out':
        return 'conclusion-timed-out';
      default:
        return '';
    }
  }

  getConclusionMessage(conclusion: string): string {
    switch (conclusion) {
      case 'action_required':
        return `Action is required (${conclusion})`;
      case 'cancelled':
        return 'Cancelled';
      case 'failure':
        return 'Failure';
      case 'neutral':
        return `Neutral (${conclusion})`;
      case 'success':
        return 'Success';
      case 'skipped':
        return `Skipped (${conclusion})`;
      case 'stale':
        return `Stale (${conclusion})`;
      case 'timed_out':
        return `Timed Out (${conclusion})`;
      default:
        return '';
    }
  }

}
